// ============================================================================
// Tour Definitions
//
// Each tour is a list of Shepherd.js steps. Steps use CSS selectors to
// attach to elements. We add `data-tour="xxx"` attributes to key elements
// so tours don't break when class names change.
//
// `advanceOn` makes the tour advance when the user clicks the target element
// (not just the "Next" button), giving a truly interactive walkthrough.
// ============================================================================

import type { StepOptions } from 'shepherd.js';
import { TOUR_IDS, type TourId } from '../stores/tourStore';

export interface TourDefinition {
  id: TourId;
  label: string;
  description: string;
  icon: string;
  /** Route where this tour should start (null = any) */
  startRoute: string | null;
  steps: StepOptions[];
}

// ---------------------------------------------------------------------------
// Dashboard Tour
// ---------------------------------------------------------------------------
const dashboardTour: TourDefinition = {
  id: TOUR_IDS.DASHBOARD,
  label: 'Dashboard Overview',
  description: 'Learn how to navigate the dashboard, customize your layout, and use key features.',
  icon: 'bi-speedometer2',
  startRoute: '/dashboard',
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to Dietech!',
      text: 'This quick tour will walk you through the main areas of the application. You can restart this tour anytime from the help menu.',
      buttons: [
        { text: 'Skip Tour', action() { this.cancel(); }, classes: 'shepherd-button-secondary' },
        { text: 'Let\'s Go!', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'sidebar',
      title: 'Navigation Sidebar',
      text: 'This is your main navigation. Access Residents, Menu Cycles, Reports, and all other areas from here. You can collapse it for more screen space.',
      attachTo: { element: '[data-tour="sidebar"]', on: 'right' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'top-toolbar',
      title: 'Top Toolbar',
      text: 'Breadcrumb navigation, theme switcher, facility selector, and notifications are all up here.',
      attachTo: { element: '[data-tour="top-toolbar"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'notification-bell',
      title: 'Notification Bell',
      text: 'This bell shows alerts for new admissions, allergy changes, diet order changes, and more. A badge appears when there are unread notifications.',
      attachTo: { element: '[data-tour="notification-bell"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'dashboard-hours',
      title: 'Time Span Filter',
      text: 'Change the time span to see dashboard data from the last few hours or days. This filters all cards at once.',
      attachTo: { element: '[data-tour="dashboard-hours"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'dashboard-customize',
      title: 'Customize Your Dashboard',
      text: 'Click "Customize Layout" to rearrange, show, or hide dashboard cards. Drag cards to reorder them. Your layout is saved per user.',
      attachTo: { element: '[data-tour="dashboard-customize"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'dashboard-cards',
      title: 'Dashboard Cards',
      text: 'Each card shows a summary of important data - diet order changes, new admissions, tray line warnings, and more. Click any card row for details.',
      attachTo: { element: '[data-tour="dashboard-grid"]', on: 'top' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'bottom-toolbar',
      title: 'Bottom Toolbar',
      text: 'Quick access to System Log and Selective Touch. The current facility and database connection are shown on the right.',
      attachTo: { element: '[data-tour="bottom-toolbar"]', on: 'top' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Done!', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Residents Tour
// ---------------------------------------------------------------------------
const residentsTour: TourDefinition = {
  id: TOUR_IDS.RESIDENTS,
  label: 'Residents Page',
  description: 'Learn how to search, filter, and manage resident records.',
  icon: 'bi-people',
  startRoute: '/people',
  steps: [
    {
      id: 'residents-intro',
      title: 'Residents Overview',
      text: 'This page shows all residents in your facility. Let\'s learn how to find and manage resident information.',
      buttons: [
        { text: 'Skip Tour', action() { this.cancel(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'residents-toolbar',
      title: 'Residents Toolbar',
      text: 'Use these buttons to add a new resident, open selected resident details, print, export data, or perform bulk operations on multiple residents.',
      attachTo: { element: '[data-tour="residents-toolbar"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'residents-grid',
      title: 'Residents Grid',
      text: 'Click any row to select a resident, or double-click to open their detail view. You can sort by clicking column headers and resize columns by dragging their edges.',
      attachTo: { element: '[data-tour="residents-grid"]', on: 'top' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'residents-search',
      title: 'Quick Search',
      text: 'Type here to instantly filter residents by name, room, diet, or any visible column. The grid updates as you type.',
      attachTo: { element: '[data-tour="residents-search"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'residents-tabs',
      title: 'Resident Detail Tabs',
      text: 'When you open a resident, you\'ll see tabs for Diet/Flucs, Meal Pattern, Weight, Notes, Skin/Wound, Tube Feed, Intake, and more. Each tab shows a focused view of that data.',
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Done!', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Selective Touch Tour
// ---------------------------------------------------------------------------
const selectiveTouchTour: TourDefinition = {
  id: TOUR_IDS.SELECTIVE_TOUCH,
  label: 'Selective Touch',
  description: 'Learn how to use the touch-friendly meal selection interface for residents.',
  icon: 'bi-hand-index-thumb',
  startRoute: '/selective-touch',
  steps: [
    {
      id: 'st-intro',
      title: 'Selective Touch',
      text: 'Selective Touch is a touch-friendly interface for recording resident meal selections. It\'s designed for tablets and kitchen staff. Let\'s walk through it.',
      buttons: [
        { text: 'Skip Tour', action() { this.cancel(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'st-header',
      title: 'Menu & Mealtime Selection',
      text: 'Choose the menu cycle, date, and mealtime at the top. This determines which menu items are available for selection.',
      attachTo: { element: '[data-tour="st-header"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'st-resident-list',
      title: 'Resident List',
      text: 'All residents for the selected unit are listed here. Tap a resident\'s name to start their meal selection. Use the search bar to quickly find someone.',
      attachTo: { element: '[data-tour="st-resident-list"]', on: 'right' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'st-menu-items',
      title: 'Menu Item Buttons',
      text: 'Tap menu items to select them for the resident. Items change color when selected. Allergen warnings appear automatically if a conflict is detected.',
      attachTo: { element: '[data-tour="st-menu-items"]', on: 'left' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'st-keyboard',
      title: 'Keyboard Shortcuts',
      text: 'Power users can use keyboard shortcuts: N/P to move between residents, Enter to save, Escape to cancel, and Tab to switch categories. Press Shift+? to see all shortcuts.',
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'st-print',
      title: 'Print Queue',
      text: 'After completing selections, you can batch-print tray tickets from the print queue. Access it from the toolbar or bottom bar.',
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Done!', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Reports Tour
// ---------------------------------------------------------------------------
const reportsTour: TourDefinition = {
  id: TOUR_IDS.REPORTS,
  label: 'Reports',
  description: 'Learn how to find, run, favorite, and schedule reports.',
  icon: 'bi-file-earmark-bar-graph',
  startRoute: '/reports',
  steps: [
    {
      id: 'reports-intro',
      title: 'Reports Center',
      text: 'The Reports page gives you access to over 100 reports - from tray tickets and diet orders to nutritional analysis and cost reports. Let\'s take a look around.',
      buttons: [
        { text: 'Skip Tour', action() { this.cancel(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-tabs',
      title: 'Reports in the Sidebar',
      text: 'The sidebar lists your Favorites, the Print Queue (where generated reports appear), and Scheduled Reports for automated delivery.',
      attachTo: { element: '[data-tour="sidebar"]', on: 'right' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-search',
      title: 'Search Reports',
      text: 'Type any keyword to instantly filter reports. Try "tray ticket", "allergy", "weight", or "production" to find what you need quickly.',
      attachTo: { element: '[data-tour="reports-search"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-view-mode',
      title: 'View Modes',
      text: 'Toggle between List, Card, and Tile views. Your preference is saved automatically.',
      attachTo: { element: '[data-tour="reports-view-mode"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-sidebar',
      title: 'Category Sidebar',
      text: 'Browse reports by category - Resident, Diet Order, Meal, Tray Line, and more. Click a category to filter, or "All Reports" to see everything.',
      attachTo: { element: '[data-tour="reports-sidebar"]', on: 'right' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-favorites',
      title: 'Favorites',
      text: 'Click the star icon on any report to add it to your favorites. Favorited reports appear in a quick-access strip at the top and in the Favorites tab.',
      attachTo: { element: '[data-tour="reports-favorites"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-customize',
      title: 'Customize Layout',
      text: 'Click the sliders icon to customize which sections, categories, and individual reports are visible. You can also reorder sections and categories.',
      attachTo: { element: '[data-tour="reports-customize"]', on: 'bottom' },
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Next', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
    {
      id: 'reports-run',
      title: 'Running a Report',
      text: 'Click any report name to open its configuration form. Set your date range, filters, and options, then click "Generate" to send it to the Print Queue for processing.',
      buttons: [
        { text: 'Back', action() { this.back(); }, classes: 'shepherd-button-secondary' },
        { text: 'Done!', action() { this.next(); }, classes: 'shepherd-button-primary' },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const ALL_TOURS: TourDefinition[] = [
  dashboardTour,
  residentsTour,
  selectiveTouchTour,
  reportsTour,
];

export function getTourById(id: TourId): TourDefinition | undefined {
  return ALL_TOURS.find((t) => t.id === id);
}
